import { useCallback } from "react";
import { NavigateOptions, To, useNavigate, useParams } from "react-router";
import { useLocation, useSearchParams } from "react-router-dom";
import { use } from "i18next";
import { getDefaultPageByIdentity } from "@/pages";
import useIdentity from "@/features/ui/hooks/use-identity";
import { OrderSheetStatus } from "@/features/order-sheets/order-sheet.type";
import { LineSheetSetType } from "@/features/line-sheet-sets/line-sheet-set.types";

export interface MyNavigateOptions extends NavigateOptions {
  defaultPage?: string;
  appendReferer?: boolean;
}

export default function useRouterHelper() {
  const identity = useIdentity();
  const { locale } = useParams();
  const sourceNavigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const calcNavigateTo = useCallback(
    (to: string, option?: MyNavigateOptions) => {
      let calcTo = to;
      if (locale && !to.startsWith(`/${locale}`)) {
        calcTo = `/${locale}${to}`;
      }

      if (option?.appendReferer) {
        let referer = "";
        if (location.search.includes("referer=")) {
          const searchParams = new URLSearchParams(location.search);
          referer = encodeURIComponent(searchParams.get("referer") || "");
        } else {
          referer = encodeURIComponent(location.pathname + location.search);
        }

        if (calcTo.includes("?")) {
          calcTo += `&referer=${referer}`;
        } else {
          calcTo += `?referer=${referer}`;
        }
      }

      return calcTo;
    },
    [locale, location.pathname, location.search]
  );

  const navigate = useCallback(
    (to: string | number, option?: MyNavigateOptions) => {
      if (typeof to === "number") {
        if (to === -1) {
          const isLast = window.history.state.idx === 0;
          if (isLast) {
            //referer가 있으면 개를 따라가게해야겟다.ㅇㅋ

            let referer;
            if (location.search.includes("referer=")) {
              const rawReferer = new URLSearchParams(location.search).get(
                "referer"
              );
              if (rawReferer) {
                referer = decodeURIComponent(rawReferer);
              }
            }

            const to = calcNavigateTo(
              referer ||
                option?.defaultPage ||
                getDefaultPageByIdentity(identity),
              option
            );
            setTimeout(() => {
              sourceNavigate(to, option);
            }, 300);
            return;
          }
        }
        sourceNavigate(to);
      } else {
        // setTimeout(() => {
        //   sourceNavigate(calcNavigateTo(to), option);
        // }, 300);
        sourceNavigate(calcNavigateTo(to, option), option);
      }
    },
    [sourceNavigate, location.search, calcNavigateTo, identity]
  );

  const navigateToLineSheetSetPage = useCallback(
    (
      lineSheetSetId: number,
      searchQuery?: string,
      madeByCompanyIds?: number[],
      seasons?: string[]
    ) => {
      let url = `/line-sheet-sets/${lineSheetSetId}?`;
      if (searchQuery) {
        url += `&query=${searchQuery}`;
      }
      if (madeByCompanyIds) {
        for (const madeByCompanyId of madeByCompanyIds) {
          url += `&made_by_company.id__in=${madeByCompanyId}`;
        }
      }
      navigate(url, { appendReferer: true });
    },
    [navigate]
  );

  const navigateToOrderSheetSetPage = useCallback(
    (
      lineSheetSetId: number,
      orderSheetSetId: number,
      searchQuery?: string,
      madeByCompanyIds?: number[],
      seasons?: string[]
    ) => {
      let url = `/line-sheet-sets/${lineSheetSetId}?order_sheet_set_id=${orderSheetSetId}`;
      if (searchQuery) {
        url += `&query=${searchQuery}`;
      }
      if (madeByCompanyIds) {
        for (const madeByCompanyId of madeByCompanyIds) {
          url += `&made_by_company.id__in=${madeByCompanyId}`;
        }
      }
      navigate(url, { appendReferer: true });
    },
    [navigate]
  );

  const navigateToBuyerOrderSheetSetPage = useCallback(
    (
      orderSheetSetId: number,
      lineSheetSetId: number,
      searchQuery?: string,
      madeByCompanyIds?: number[],
      seasons?: string[],
      editable?: boolean,
      replace?: boolean,
      appendReferer: boolean = true
    ) => {
      let url = `/order-sheets/${orderSheetSetId}?line_sheet_set_id=${lineSheetSetId}`;
      if (searchQuery) {
        url += `&query=${searchQuery}`;
      }
      if (madeByCompanyIds) {
        for (const madeByCompanyId of madeByCompanyIds) {
          url += `&made_by_company.id__in=${madeByCompanyId}`;
        }
      }
      if (editable) {
        url += `&editable=true`;
      }
      navigate(url, { appendReferer, replace });
    },
    [navigate]
  );

  const navigateToOrderSheetSetDraftPage = useCallback(
    (
      orderSheetSetId: number,
      orderSheetId?: number,
      lineSheetId?: number,
      madeByCompanyIds?: number[],
      seasons?: string[],
      searchQuery?: string
    ) => {
      if (orderSheetId) {
        let url = `/order-sheets/${orderSheetSetId}?order_sheet_id=${orderSheetId}`;
        if (madeByCompanyIds) {
          for (const madeByCompanyId of madeByCompanyIds) {
            url += `&made_by_company.id__in=${madeByCompanyId}`;
          }
        }
        if (seasons) {
          for (const season of seasons) {
            url += `&season__in=${season}`;
          }
        }
        if (searchQuery) {
          url += `&query=${searchQuery}`;
        }
        navigate(url, { appendReferer: true });
      } else if (lineSheetId) {
        let url = `/order-sheets/${orderSheetSetId}?line_sheet_id=${lineSheetId}`;
        if (madeByCompanyIds) {
          for (const madeByCompanyId of madeByCompanyIds) {
            url += `&made_by_company.id__in=${madeByCompanyId}`;
          }
        }
        if (searchQuery) {
          url += `&query=${searchQuery}`;
        }
        navigate(url, { appendReferer: true });
      }
    },
    [navigate]
  );

  const navigateToLineSheetSetDraftPage = useCallback(
    (
      lineSheetSetId: number,
      lineSheetId: number,
      madeByCompanyIds?: number[],
      seasons?: string[],
      searchQuery?: string
    ) => {
      // navigate(`/line-sheets/${lineSheetSetId}?line_sheet_id=${lineSheetId}`, {
      //   appendReferer: true,
      // });
      let url = `/line-sheets/${lineSheetSetId}?line_sheet_id=${lineSheetId}`;
      if (madeByCompanyIds) {
        for (const madeByCompanyId of madeByCompanyIds) {
          url += `&made_by_company.id__in=${madeByCompanyId}`;
        }
      }
      if (searchQuery) {
        url += `&query=${searchQuery}`;
      }
      navigate(url, { appendReferer: true });
    },
    [navigate]
  );

  const navigateToLineSheetPage = useCallback(
    (query?: string, brand?: number, supplierId?: number) => {
      let url = `/line-sheets?type__eq=STOCK`;
      if (query) {
        url += `&query=${query}`;
      }
      if (brand) {
        url += `&made_by_company.id__in=${brand}`;
      }
      if (supplierId) {
        url += `&issued_by_company.id__in=${supplierId}`;
      }

      navigate(url);
    },
    [navigate]
  );

  // 패스픽 상품 찾기. query 는 검색창(usePathPickProductFilter 의 query)에 그대로 들어간다.
  const navigateToPathPickSearchProductsPage = useCallback(
    (query?: string) => {
      const params = new URLSearchParams();
      if (query) params.set("query", query);
      const search = params.toString();
      navigate(`/pathpick/search-products${search ? `?${search}` : ""}`);
    },
    [navigate]
  );

  const navigateToApiOrderPage = useCallback(
    (query?: string) => {
      let url = `/line-sheets/api-order`;

      if (query) {
        url += `?search_query=${query}`;
      }

      navigate(url);
    },
    [navigate]
  );

  const navigateToApiOrderSheetPage = useCallback(
    (brand_id?: number, query?: string) => {
      if (!brand_id) return;

      let url = `/order-sheets/api-order-sheet?brand_id=${brand_id}`;
      if (query) {
        url += `&query=${query}`;
      }

      navigate(url);
    },
    [navigate]
  );

  const navigateToApiOrderSheetDetailPage = useCallback(
    (brand_id: number, order_id: number) => {
      let url = `/order-sheet-sets/api-order-detail/?brand_id=${brand_id}&order_id=${order_id}`;

      navigate(url);
    },
    [navigate]
  );

  const navigateToOrderHistoryDetailPage = useCallback(
    (id: number, status?: OrderSheetStatus, type?: LineSheetSetType) => {
      let url = `/order-sheet-sets/order-history?order_sheet_set_id=${id}`;
      if (status) {
        url += `&status=${status}`;
      }
      if (type) {
        url += `&line_sheet_set.type__eq=${type}`;
      }

      navigate(url);
    },
    [navigate]
  );

  const navigateToOrderHistorySettlementFeePage = useCallback(
    (date: Date) => {
      let url = `/order-sheets/settlement?type=FEE`;
      if (date) {
        url += `&date=${date}`;
      }

      navigate(url);
    },
    [navigate]
  );

  return {
    navigate,
    navigateToLineSheetSetPage,
    navigateToOrderSheetSetPage,
    navigateToBuyerOrderSheetSetPage,
    navigateToLineSheetSetDraftPage,
    navigateToOrderSheetSetDraftPage,
    navigateToLineSheetPage,
    navigateToApiOrderPage,
    navigateToPathPickSearchProductsPage,
    navigateToApiOrderSheetDetailPage,
    calcNavigateTo,
    location,
    searchParams,
    navigateToApiOrderSheetPage,
    navigateToOrderHistoryDetailPage,
    navigateToOrderHistorySettlementFeePage,
  };
}
